import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Email } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { formatInvoiceDate, formatNumberToCurrency, formatPeriod } from '../../helpers/utils';
import InvoiceStatusRenderer from './InvoiceStatusRenderer';
import {
  downloadInvoicePdf,
  viewInvoicePdf,
  executeInvoiceAction,
  getTenantInvoices
} from '../../store/actions/tenantsAction';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { isAfter, parse } from 'date-fns';
import Chip from '@mui/material/Chip';

const TenantInvoices = ({ tenantId }) => {
  const invoices = useSelector((state) => state.tenants.tenantInvoices.data || []);
  const dispatch = useDispatch();

  const nextInvoiceId = useMemo(() => {
    const today = new Date();
    const parseFormat = "yyyy-MM-dd HH:mm:ss xx 'UTC'";

    const nextInvoice = invoices.find((invoice) => {
      const invoiceDate = parse(invoice.periodstartdate, parseFormat, new Date());
      return isAfter(invoiceDate, today);
    });

    return nextInvoice?.id ?? null;
  }, [invoices]);

  useEffect(() => {
    dispatch(getTenantInvoices(tenantId));
  }, [dispatch, tenantId]);

  const onDownload = (id) => {
    dispatch(downloadInvoicePdf(id));
  };

  const onView = (id) => {
    dispatch(viewInvoicePdf(id));
  };

  const onApprove = (id) => {
    dispatch(executeInvoiceAction(id, 'APPROVE')).then(() => dispatch(getTenantInvoices(tenantId)));
  };

  const onEmail = (id) => {
    dispatch(executeInvoiceAction(id, 'SEND')).then(() => dispatch(getTenantInvoices(tenantId)));
  };

  const onPaid = (id) => {
    dispatch(executeInvoiceAction(id, 'PAID')).then(() => dispatch(getTenantInvoices(tenantId)));
  };

  const formatDate = (invoice) => formatInvoiceDate(invoice.invoicedate);

  const renderActions = (invoice) => {
    const { status, id } = invoice;

    return (
      <Stack direction="row" spacing={3}>
        <Tooltip title="View Invoice">
          <IconButton
            onClick={() => onView(id)}
            sx={{
              color: '#7b1fa2',
              border: '1px solid #7b1fa2',
              padding: '4px',
              '&:hover': { backgroundColor: 'rgba(123, 31, 162, 0.04)' }
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Download Invoice">
          <IconButton
            onClick={() => onDownload(id)}
            sx={{
              color: '#7b1fa2',
              border: '1px solid #7b1fa2',
              padding: '4px',
              '&:hover': { backgroundColor: 'rgba(123, 31, 162, 0.04)' }
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {status === 'Pending Approval' && (
          <Button variant="contained" color="success" size="small" onClick={() => onApprove(id)}>
            Approve
          </Button>
        )}

        {(status === 'Approved' || status === 'Send') && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Email />}
              onClick={() => onEmail(id)}
            >
              Email
            </Button>
            <Button variant="contained" color="primary" size="small" onClick={() => onPaid(id)}>
              Paid
            </Button>
          </>
        )}

        {status === 'Paid' && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            No further actions
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 3 }}>
      <Table sx={{ minWidth: 650 }} aria-label="tenant invoices table">
        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
          <TableRow>
            <TableCell>
              <strong>Invoice Date</strong>
            </TableCell>
            <TableCell>
              <strong>Period</strong>
            </TableCell>
            <TableCell>
              <strong>Amount</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Status</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Actions</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>
                {formatDate(row)}
                {nextInvoiceId === row.id && (
                  <Chip
                    sx={{marginLeft: "10px"}}
                    label="Next"
                    color="warning"
                    size="small"
                  />
                )}
              </TableCell>
              <TableCell>{formatPeriod(row.periodstartdate, row.periodenddate)}</TableCell>
              <TableCell>{formatNumberToCurrency(row.invoicepaymentvalue)}</TableCell>
              <TableCell>
                <InvoiceStatusRenderer value={row.status} />
              </TableCell>
              <TableCell align="right">{renderActions(row)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TenantInvoices;
